import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  ApiError,
  SdkNotificationConsent,
  SdkNotificationConsentChannels,
  SdkNotificationConsentHandle,
  SdkUseNotificationConsentOptions
} from '@Pages/pageRuntime/sdk/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

export const NOTIFICATION_CONSENT_URL = '/api/notification-consents';
export const NOTIFICATION_CONSENT_QUERY_KEY = 'notification-consents';

type Target = { resource_id?: string; record_id?: string };

function target(options?: SdkUseNotificationConsentOptions): Target {
  return options?.resourceId && options.recordId
    ? { resource_id: options.resourceId, record_id: options.recordId }
    : {};
}

export function useNotificationConsent(
  options?: SdkUseNotificationConsentOptions
): SdkNotificationConsentHandle {
  const queryClient = useQueryClient();
  const params = target(options);
  const queryKey = [PAGE_API_QUERY_KEY_HEAD, NOTIFICATION_CONSENT_QUERY_KEY, params] as const;

  const query = useQuery<SdkNotificationConsent, ApiError>({
    enabled: options?.enabled ?? true,
    queryKey,
    queryFn: () =>
      axios.get<SdkNotificationConsent>(NOTIFICATION_CONSENT_URL, { params }).then(
        (response) => response.data,
        (error: unknown) => {
          throw toApiError(error);
        }
      )
  });

  const mutation = useMutation<SdkNotificationConsent, ApiError, SdkNotificationConsentChannels>({
    mutationFn: (channels) =>
      axios.put<SdkNotificationConsent>(NOTIFICATION_CONSENT_URL, { ...params, channels }).then(
        (response) => response.data,
        (error: unknown) => {
          throw toApiError(error);
        }
      ),
    onSuccess: (consent) => {
      queryClient.setQueryData(queryKey, consent);
      options?.onSuccess?.(consent);
    },
    onError: (error) => options?.onError?.(error)
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    update: (channels) =>
      mutation.mutateAsync(channels).then(
        (consent) => consent ?? null,
        () => null
      ),
    isPending: mutation.isPending,
    updateError: mutation.error,
    reset: () => {
      mutation.reset();
    }
  };
}
